import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, User, MapPin, Phone, Clock, CheckCircle2, Truck, AlertCircle, ChevronRight } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { useVendor } from '../context/VendorContext';
import { Header } from '../components/ui/Header';
import { Button } from '../components/ui/Button';
export function VendorOrderDetailScreen() {
  const {
    currentParams,
    goBack
  } = useNavigation();
  const {
    orders,
    updateOrderStatus
  } = useVendor();
  const [loading, setLoading] = useState(false);
  const orderId = currentParams?.orderId;
  const order = orders.find(o => o.id === orderId);
  if (!order) {
    return <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-6" data-id="element-2065">
        <AlertCircle className="w-16 h-16 text-gray-400 mb-4" data-id="element-2066" />
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-2067">
          Order Not Found
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-center mb-6" data-id="element-2068">
          The order you are looking for does not exist or has been removed.
        </p>
        <Button onClick={goBack} data-id="element-2069">Go Back</Button>
      </div>;
  }
  const handleStatusUpdate = (newStatus: typeof order.orderStatus) => {
    setLoading(true);
    setTimeout(() => {
      updateOrderStatus(order.id, newStatus);
      setLoading(false);
    }, 1000);
  };
  const timeline = [{
    status: 'New',
    label: 'Placed',
    icon: Package
  }, {
    status: 'Processing',
    label: 'Confirmed',
    icon: CheckCircle2
  }, {
    status: 'Shipped',
    label: 'Shipped',
    icon: Truck
  }, {
    status: 'Delivered',
    label: 'Delivered',
    icon: MapPin
  }];
  const currentStatusIndex = timeline.findIndex(t => t.status === order.orderStatus);
  const isCancelled = order.orderStatus === 'Cancelled';
  return <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24" data-id="element-2070">
      <Header title={`Order #${order.id}`} showBack data-id="element-2071" />

      <div className="p-4 space-y-4" data-id="element-2072">
        {/* Status Banner */}
        <div className={`p-4 rounded-2xl text-white shadow-sm flex items-center justify-between ${order.orderStatus === 'Delivered' ? 'bg-green-500' : order.orderStatus === 'Shipped' ? 'bg-blue-500' : order.orderStatus === 'Processing' ? 'bg-primary' : order.orderStatus === 'Cancelled' ? 'bg-red-500' : 'bg-yellow-500'}`} data-id="element-2073">
          <div data-id="element-2074">
            <h2 className="text-lg font-bold" data-id="element-2075">{order.orderStatus}</h2>
            <p className="text-sm opacity-90" data-id="element-2076">
              {new Date(order.date).toLocaleString()}
            </p>
          </div>
          <Package className="w-8 h-8 opacity-50" data-id="element-2077" />
        </div>

        {/* Timeline (if not cancelled) */}
        {!isCancelled && <div className="bg-white dark:bg-gray-800 p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-2078">
            <div className="flex justify-between relative" data-id="element-2079">
              <div className="absolute top-4 left-4 right-4 h-0.5 bg-gray-100 dark:bg-gray-700 -z-10" data-id="element-2080"></div>
              <div className="absolute top-4 left-4 h-0.5 bg-primary -z-10 transition-all duration-500" style={{
            width: `${Math.max(0, currentStatusIndex) / (timeline.length - 1) * 100}%`
          }} data-id="element-2081"></div>

              {timeline.map((step, idx) => {
            const isCompleted = currentStatusIndex >= idx;
            const isCurrent = currentStatusIndex === idx;
            return <div key={step.status} className="flex flex-col items-center gap-2" data-id="element-2082">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${isCompleted ? 'bg-primary border-primary text-white' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400'}`} data-id="element-2083">
                      <step.icon className="w-4 h-4" data-id="element-2084" />
                    </div>
                    <span className={`text-[10px] font-medium ${isCurrent ? 'text-primary' : isCompleted ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`} data-id="element-2085">
                      {step.label}
                    </span>
                  </div>;
          })}
            </div>
          </div>}

        {/* Buyer Info */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-2086">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4" data-id="element-2087">
            Customer Details
          </h3>
          <div className="space-y-3" data-id="element-2088">
            <div className="flex items-center gap-3" data-id="element-2089">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary" data-id="element-2090">
                <User className="w-5 h-5" data-id="element-2091" />
              </div>
              <div data-id="element-2092">
                <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2093">
                  {order.buyerName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2094">
                  Customer
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-2095">
              <div className="w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center text-gray-500" data-id="element-2096">
                <Phone className="w-5 h-5" data-id="element-2097" />
              </div>
              <p className="text-sm text-gray-900 dark:text-white" data-id="element-2098">
                {order.buyerPhone}
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-2099">
              <div className="w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center text-gray-500" data-id="element-2100">
                <MapPin className="w-5 h-5" data-id="element-2101" />
              </div>
              <p className="text-sm text-gray-900 dark:text-white" data-id="element-2102">
                {order.shippingAddress}
              </p>
            </div>
          </div>
        </div>

        {/* Product Info */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-2103">
          <h3 className="font-bold text-gray-900 dark:text-white mb-4" data-id="element-2104">
            Order Items
          </h3>
          <div className="flex gap-3" data-id="element-2105">
            <img src={order.product.image} alt={order.product.name} className="w-20 h-20 rounded-xl object-cover bg-gray-50 dark:bg-gray-900" data-id="element-2106" />
            <div className="flex-1" data-id="element-2107">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2" data-id="element-2108">
                {order.product.name}
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-2109">
                Qty: {order.product.quantity}
              </p>
              <p className="text-sm font-bold text-primary mt-1" data-id="element-2110">
                ₦{order.product.price.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Payment Summary */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-2111">
          <div className="flex justify-between items-center mb-4" data-id="element-2112">
            <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-2113">
              Payment Summary
            </h3>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${order.paymentStatus === 'Paid' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : order.paymentStatus === 'COD' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`} data-id="element-2114">
              {order.paymentStatus}
            </span>
          </div>
          <div className="space-y-2 text-sm" data-id="element-2115">
            <div className="flex justify-between text-gray-600 dark:text-gray-400" data-id="element-2116">
              <span data-id="element-2117">Subtotal</span>
              <span data-id="element-2118">
                ₦
                {(order.product.price * order.product.quantity).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between text-gray-600 dark:text-gray-400" data-id="element-2119">
              <span data-id="element-2120">Shipping Fee</span>
              <span data-id="element-2121">₦4,500</span>
            </div>
            <div className="pt-2 mt-2 border-t border-gray-100 dark:border-gray-700 flex justify-between font-bold text-gray-900 dark:text-white" data-id="element-2122">
              <span data-id="element-2123">Total</span>
              <span className="text-primary text-lg" data-id="element-2124">
                ₦{order.total.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Bottom Bar */}
      {!isCancelled && order.orderStatus !== 'Delivered' && <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 p-4 pb-safe z-40" data-id="element-2125">
          {order.orderStatus === 'New' && <div className="flex gap-3" data-id="element-2126">
              <Button variant="outline" className="flex-1 border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900/30 dark:text-red-400 dark:hover:bg-red-900/10" onClick={() => handleStatusUpdate('Cancelled')} loading={loading} data-id="element-2127">
                Reject
              </Button>
              <Button className="flex-1 bg-primary hover:bg-primary/90 text-white" onClick={() => handleStatusUpdate('Processing')} loading={loading} data-id="element-2128">
                Accept Order
              </Button>
            </div>}
          {order.orderStatus === 'Processing' && <Button fullWidth className="bg-blue-500 hover:bg-blue-600 text-white" onClick={() => handleStatusUpdate('Shipped')} loading={loading} data-id="element-2129">
              Mark as Shipped
            </Button>}
          {order.orderStatus === 'Shipped' && <Button fullWidth className="bg-green-500 hover:bg-green-600 text-white" onClick={() => handleStatusUpdate('Delivered')} loading={loading} data-id="element-2130">
              Mark as Delivered
            </Button>}
        </div>}
    </div>;
}